/**
 * Shared TagOnCompletion Component
 *
 * Reusable component for managing completion tags across survey types.
 * Used by: SatisfactionSurvey, CustomSurvey, EventRegistrationSurvey
 *
 * Features:
 * - Toggle to enable/disable completion tagging
 * - Tag editor integration with TagsListWithTagsEditor
 * - Form validation and error handling
 * - Support for effective completion tags
 * - Hover card with helpful information
 * - Cross-survey type compatibility
 */

import * as Api from '@ViewModels';
import { css, StyleSheet } from 'aphrodite';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { TagsListWithTagsEditor } from '../../../../../components/entities/tags/TagsEditor';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '../../../../../components/HoverCard';
import { InfoIcon } from '../../../../../components/svgs/icons/InfoIcon';
import { Toggle } from '../../../../../components/Toggle';
import { grayIconFill } from '../../../../../styles/colors';
import { bs } from '../../../../../styles/styles';
import { CustomSurveyFormData } from '../../../CustomSurvey/utils';
import { DonationSurveyFormData } from '../../../DonationSurvey/utils';
import { EventRegistrationFormData } from '../../../EventRegistrationSurvey/utils';
import { ISatisfactionFormData } from '../../../SatisfactionSurvey/utils';
import { usePreviewMode } from '../../contexts/PreviewModeContext';
import { FormFieldWrapper } from '../FormFieldWrapper';

// Union type for all supported form data types
type SurveyFormData = CustomSurveyFormData | DonationSurveyFormData | EventRegistrationFormData | ISatisfactionFormData;

// Constants for form field names and component IDs
const FORM_FIELD_NAMES = {
	TAG_ON_COMPLETION: 'tagGenerationSettings.tagOnCompletion' as const,
	COMPLETION_TAG: 'tagGenerationSettings.completionTag' as const,
} as const;

const COMPONENT_IDS = {
	TOGGLE: 'tag-on-completion-checkbox',
	TAG_EDITOR_SECTION: 'tag-editor-section',
} as const;

/**
 * Props for TagOnCompletion component
 */
interface TagOnCompletionProps {
	className?: string;
}

/**
 * Shared TagOnCompletion component for managing completion tags
 */
export const TagOnCompletion: React.FC<TagOnCompletionProps> = ({ className }) => {
	const { control, watch, setValue, formState, setError, clearErrors } = useFormContext<SurveyFormData>();
	const { isPreviewOnly } = usePreviewMode();
	const formValues = watch();

	const [tags, setTags] = React.useState<Api.IAccountTag[]>([]);

	// Sync tags with effective completion tag from form
	React.useEffect(() => {
		if (formValues?.tagGenerationSettings?.effectiveCompletionTag) {
			setTags([formValues.tagGenerationSettings.effectiveCompletionTag]);
		}
	}, [formValues?.tagGenerationSettings?.effectiveCompletionTag]);

	const onRequestAddTag = React.useCallback(
		(tag: Api.IAccountTag) => {
			if (!tag?.name) {
				setError(FORM_FIELD_NAMES.COMPLETION_TAG, {
					message: 'Please select a valid tag',
				});
				return;
			}
			setTags([tag]);
			setValue(FORM_FIELD_NAMES.COMPLETION_TAG, tag.name);
			clearErrors(FORM_FIELD_NAMES.COMPLETION_TAG);
		},
		[setValue, setError, clearErrors]
	);

	const onRequestRemoveTag = React.useCallback(() => {
		setValue(FORM_FIELD_NAMES.COMPLETION_TAG, '');
		setTags([]);
	}, [setValue]);

	const handleToggleChange = React.useCallback(
		(fieldProps: { value: boolean; onChange: (value: boolean) => void }) => {
			const newValue = !fieldProps.value;
			fieldProps.onChange(newValue);
			// Clear any existing errors when toggling
			if (newValue) {
				// When turning on, clear any validation errors since user hasn't had a chance to select a tag yet
				clearErrors(FORM_FIELD_NAMES.COMPLETION_TAG);
			} else {
				// When turning off, clear the completion tag value and any errors
				setValue(FORM_FIELD_NAMES.COMPLETION_TAG, '');
				setTags([]);
				clearErrors(FORM_FIELD_NAMES.COMPLETION_TAG);
			}
		},
		[clearErrors, setValue]
	);

	return (
		<div className={className ? `${css(styles.formControl)} ${className}` : css(styles.formControl)}>
			<FormFieldWrapper
				name={FORM_FIELD_NAMES.TAG_ON_COMPLETION}
				control={control}
				title='Add Completion Tag'
				description='Add a tag to survey responses'
			>
				{({ value, onChange }) => (
					<Toggle
						id={COMPONENT_IDS.TOGGLE}
						isOn={value}
						disabled={isPreviewOnly}
						onToggleCheckChanged={() => handleToggleChange({ value, onChange })}
						uncheckedColor={grayIconFill}
						aria-describedby={value ? COMPONENT_IDS.TAG_EDITOR_SECTION : undefined}
					/>
				)}
			</FormFieldWrapper>
			<div>
				{formValues?.tagGenerationSettings?.tagOnCompletion && (
					<div
						id={COMPONENT_IDS.TAG_EDITOR_SECTION}
						className={css(bs.flex, bs.flexRow)}
						role='group'
						aria-label='Tag selection'
					>
						<TagsListWithTagsEditor
							tagType={Api.ResourceAutoCompleteViewModelType.AccountTag}
							entity={undefined}
							onRequestAddTag={onRequestAddTag}
							onRequestRemoveTag={onRequestRemoveTag}
							tags={tags || []}
							showInputEditor={!tags.length && !isPreviewOnly}
							disabled={isPreviewOnly}
						/>
						{tags.length > 0 ? (
							<div className={css(bs.flex, bs.flexCenter, bs.ml2)}>
								<HoverCard openDelay={100}>
									<HoverCardTrigger>
										<InfoIcon className={css(styles.infoIcon)} />
									</HoverCardTrigger>
									<HoverCardContent side='right'>
										<p className={css(bs.textSm, bs.m0, bs.textDarkGrayFontColor)}>
											Adding a tag will remove the current tag
										</p>
									</HoverCardContent>
								</HoverCard>
							</div>
						) : null}
					</div>
				)}
				<FormFieldWrapper
					name={FORM_FIELD_NAMES.COMPLETION_TAG}
					control={control}
					error={formState.errors?.tagGenerationSettings?.completionTag}
					showError={true}
				>
					{() => null}
				</FormFieldWrapper>
			</div>
		</div>
	);
};

/**
 * Styles for TagOnCompletion component
 */
const styles = StyleSheet.create({
	formControl: {
		display: 'flex',
		flexDirection: 'column' as const,
		width: '100%',
		marginBottom: '20px',
	},

	infoIcon: {
		width: '16px',
		height: '16px',
		color: '#666666',
		cursor: 'help',

		':hover': {
			color: '#0066cc',
		},
	},

	errorMessage: {
		color: '#dc3545',
		fontSize: '14px',
		marginTop: '8px',
		padding: '8px',
		backgroundColor: '#fff5f5',
		border: '1px solid #fed7d7',
		borderRadius: '4px',
		display: 'flex',
		alignItems: 'center',
	},
});

/**
 * Type exports for consumers
 */
export type { SurveyFormData, TagOnCompletionProps };
